import { computed, inject, Injectable, Signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ComponentStore } from '@ngrx/component-store';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import {
  IValidationResult,
  PracticeSetupStepValidation,
  type IPageVisitTrackingConfig,
  type IPracticeSetupStepDefinition,
} from '@principle-theorem/principle-core';
import {
  IPracticeSetupStep,
  PracticeSetupStepStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  initMoment,
  ISODateType,
  toMoment,
} from '@principle-theorem/shared';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PracticeSetupWizardStore } from './setup-wizard.store';

interface ISetupStepState {
  step: IPracticeSetupStep | undefined;
  validationResult: IValidationResult | undefined;
}

const initialState: ISetupStepState = {
  step: undefined,
  validationResult: undefined,
};

@Injectable()
export class SetupStepStore extends ComponentStore<ISetupStepState> {
  private _organisationService = inject(OrganisationService);
  private _wizardStore = inject(PracticeSetupWizardStore);

  readonly step = this.selectSignal((state) => state.step);
  readonly validationResult = this.selectSignal(
    (state) => state.validationResult
  );

  readonly definition: Signal<IPracticeSetupStepDefinition | undefined> =
    computed(() => {
      const currentStep = this.step();
      if (!currentStep) {
        return;
      }
      return this._wizardStore
        .regionFilteredSteps()
        .find((regionStep) => regionStep.id === currentStep.id);
    });

  readonly isComplete = computed(
    () => this.step()?.status === PracticeSetupStepStatus.Complete
  );

  readonly isOverdue = computed(() => {
    const step = this.step();
    if (!step?.dueDate || this.isComplete()) {
      return false;
    }
    return step.dueDate ? toMoment(step.dueDate).isBefore(initMoment()) : false;
  });

  readonly canComplete = computed(() => {
    const validationResult = this.validationResult();
    if (validationResult) {
      return validationResult.valid;
    }
    const step = this.step();
    if (!step) {
      return false;
    }
    const definition = this.definition();
    return !definition?.validate$;
  });

  readonly canReopen = computed(() => {
    return (
      !this.canComplete() &&
      this.step()?.status === PracticeSetupStepStatus.Complete
    );
  });

  readonly liveValidationErrors: Signal<string[] | undefined> = computed(() => {
    const result = this.validationResult();
    if (!result) {
      return;
    }
    return result.valid
      ? undefined
      : result.reason
        ? [result.reason]
        : ['Validation failed'];
  });

  readonly mutating = computed(() => this._wizardStore.mutating());

  readonly stepProgress: Signal<
    { visitedCount: number; requiredCount: number } | undefined
  > = computed(() => {
    const step = this.step();
    const tracking = this.definition()?.pageVisitTracking;

    if (
      !step ||
      !tracking ||
      (tracking.validationMode !== 'count' && tracking.validationMode !== 'all')
    ) {
      return;
    }

    const validationProgress = this.validationResult()?.progress;
    const visitedCount =
      validationProgress?.current ??
      this._getMatchingVisitCount(step, tracking);
    let requiredCount = tracking.requiredCount ?? validationProgress?.total;

    if (!requiredCount || requiredCount <= 0) {
      requiredCount = Math.max(visitedCount, 1);
    }

    const displayVisitedCount =
      step.status === PracticeSetupStepStatus.Complete
        ? requiredCount
        : Math.min(visitedCount, requiredCount);

    return {
      visitedCount: displayVisitedCount,
      requiredCount,
    };
  });

  constructor() {
    super(initialState);

    toObservable(this.step)
      .pipe(
        switchMap((step) => {
          const regionSteps = this._wizardStore.regionFilteredSteps();
          const definition = step
            ? regionSteps.find((matchingStep) => matchingStep.id === step.id)
            : undefined;

          if (!step || !definition?.validate$) {
            return of(undefined);
          }

          const practice$ =
            this._organisationService.practice$.pipe(filterUndefined());
          return definition.validate$(practice$, step);
        }),
        takeUntilDestroyed()
      )
      .subscribe((result) => {
        this.patchState({ validationResult: result });
      });
  }

  setStep(step: IPracticeSetupStep | undefined): void {
    this.patchState({ step });
  }

  formatDueDate(dueDate?: ISODateType): string {
    if (!dueDate) {
      return '';
    }
    const now = initMoment();
    const diffDays = toMoment(dueDate)
      .startOf('day')
      .diff(now.startOf('day'), 'days');

    if (diffDays < 0) {
      return `Overdue by ${Math.abs(diffDays)} day${
        Math.abs(diffDays) !== 1 ? 's' : ''
      }`;
    }
    if (diffDays === 0) {
      return 'Due today';
    }
    if (diffDays === 1) {
      return 'Due tomorrow';
    }
    return `Due in ${diffDays} days`;
  }

  getStepProgressLabel(): string {
    const progress = this.stepProgress();
    if (!progress) {
      return '';
    }
    return `${progress.visitedCount}/${progress.requiredCount}`;
  }

  reopenStep(): void {
    const step = this.step();
    if (!step || !this.canReopen()) {
      return;
    }
    this._wizardStore.reopenStep(step.id);
  }

  private _getMatchingVisitCount(
    step: IPracticeSetupStep,
    tracking: IPageVisitTrackingConfig
  ): number {
    const matchingRoutes = new Set<string>();

    for (const visit of step.pageVisits ?? []) {
      if (
        tracking.validationMode === 'count' ||
        tracking.validationMode === 'all'
      ) {
        if (PracticeSetupStepValidation.isCountableMatch(visit, tracking)) {
          matchingRoutes.add(visit.route);
        }
      } else if (
        PracticeSetupStepValidation.hasMatchingRouteSegment(
          visit,
          tracking.routes
        )
      ) {
        matchingRoutes.add(visit.route);
      }
    }

    return matchingRoutes.size;
  }
}
